import React, {useState, useEffect} from 'react';
import {Formik, Form, Field, ErrorMessage} from 'formik';
import * as Yup from 'yup';
import {useNavigate, useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {IoChevronDown, IoChevronUp} from 'react-icons/io5';
import "pages/users/_userDetail.scss";
import {ROLES, ROLE_DISPLAY} from 'common/constants';
import {api} from 'api/calls';
import ComLoader from 'components/comLoader/ComLoader';
import {routes} from 'common';
import {userValidationSchema} from 'pages/users/constants';
import {Loader, ConfirmModal} from 'components';
import {generalExtraActions} from "../../reduxStore/actions";
import SingleSelect from "../../components/singleSelect/SingleSelect";
import MultiSelect from "../../components/multiSelect/MultiSelect";

function UserDetail() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {id} = useParams();
  const {
    subRolesByRole,
    subRolesByRoleLoading,
    subRolesByRoleError,
    grounds,
    groundsLoading,
    groundsError
  } = useSelector((state) => state.general);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [expandedLocation, setExpandedLocation] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const initialValues = {
    full_name: userData?.full_name || '',
    email: userData?.email || '',
    phone_number: userData?.phone_number || '',
    role: userData?.role || ROLES.PLAYER,
    sub_role: userData?.sub_role || '',
    grounds: userData?.grounds || [],
    is_active: userData?.is_active ?? true,
    // dob: userData?.dob || '',
    // location: {
    //   city: userData?.location?.city || '',
    //   address: userData?.location?.address || '',
    //   latitude: userData?.location?.latitude || '',
    //   longitude: userData?.location?.longitude || '',
    // },
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await api.updateUser(id, values);
      navigate(routes.USERS);
      toast.success('User updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await api.deleteUser(id);
      toast.success('User deleted successfully!');
      navigate('/users');
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleCancel = () => {
    navigate(routes.USERS);
  };

  const toggleLocationExpansion = () => {
    setExpandedLocation(!expandedLocation);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setFetchLoading(true);
        const response = await api.getUserDetail(id);
        setUserData(response);
      } catch (error) {
        navigate('/users');
      } finally {
        setFetchLoading(false);
      }
    };

    if (id) {
      fetchUserData();
    }
  }, [id, navigate]);


  useEffect(() => {
    dispatch(generalExtraActions.getCities());
    dispatch(generalExtraActions.getSubRolesByRole());
    dispatch(generalExtraActions.getGrounds());
  }, []);

  if (!userData && !fetchLoading) {
    return (
      <div className="user-detail-page detail-page">
        <div className="error-container">
          <p>User not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-detail-page detail-page">
      {fetchLoading && (<Loader/>)}
      <div className="header">
        <h1>Edit User</h1>
        <p className="subtitle">Update user information and settings</p>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={userValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({values, errors, touched, setFieldValue}) => {
          return (
            <Form className="custom-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="full_name">
                    Full Name <span className="required">*</span>
                  </label>
                  <Field
                    type="text"
                    id="full_name"
                    name="full_name"
                    className={`form-control ${touched.full_name && errors.full_name ? 'is-invalid' : ''}`}
                    placeholder="Enter full name"
                  />
                  <ErrorMessage name="full_name" component="div" className="error-message"/>
                </div>
                <div className="form-group">
                  <label htmlFor="email">
                    Email <span className="required">*</span>
                  </label>
                  <Field
                    type="email"
                    id="email"
                    name="email"
                    className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                    placeholder="Enter email address"
                  />
                  <ErrorMessage name="email" component="div" className="error-message"/>
                </div>

              </div>

              <div className="form-row">

                <div className="form-group">
                  <label htmlFor="phone_number">
                    Phone Number <span className="required">*</span>
                  </label>
                  <Field
                    type="tel"
                    id="phone_number"
                    name="phone_number"
                    className={`form-control ${touched.phone_number && errors.phone_number ? 'is-invalid' : ''}`}
                    placeholder="+923221234567"
                  />
                  <ErrorMessage name="phone_number" component="div" className="error-message"/>
                </div>
                <div className="form-group">
                  <label htmlFor="is_active">
                    Status <span className="required">*</span>
                  </label>
                  <Field
                    as="select"
                    id="is_active"
                    name="is_active"
                    className={`form-control ${touched.is_active && errors.is_active ? 'is-invalid' : ''}`}
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </Field>
                  <ErrorMessage name="is_active" component="div" className="error-message"/>
                </div>

              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="role">
                    Role <span className="required">*</span>
                  </label>
                  <Field
                    as="select"
                    id="role"
                    name="role"
                    className={`form-control ${touched.role && errors.role ? 'is-invalid' : ''}`}
                  >
                    {Object.entries(ROLE_DISPLAY).map(([key, display]) => (
                      <option key={key} value={key}>
                        {display}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="role" component="div" className="error-message"/>
                </div>

              </div>

              {(values.role === ROLES.ADMIN || values.role === ROLES.GROUND_ADMIN) && (
                <div className="form-group">
                  <label htmlFor="sub_role">
                    Subrole <span className="required">*</span>
                  </label>
                  {subRolesByRoleLoading ? (
                    <div style={{color: '#888', padding: '8px 0'}}>Loading subroles...</div>
                  ) : subRolesByRoleError ? (
                    <div style={{color: '#d32f2f', padding: '8px 0'}}>Failed to load subroles</div>
                  ) : (
                    <SingleSelect
                      options={
                        values.role === ROLES.ADMIN
                          ? subRolesByRole.admin?.map((sub_role) => ({
                          label: sub_role.name,
                          value: sub_role.id
                        })) || []
                          : subRolesByRole.ground_admin?.map((sub_role) => ({
                          label: sub_role.name,
                          value: sub_role.id
                        })) || []
                      }
                      value={values.sub_role || ''}
                      onChange={(subroleId) => setFieldValue('sub_role', subroleId)}
                      placeholder="Select a subrole"
                      isDisabled={false}
                    />
                  )}
                  <ErrorMessage name="sub_role" component="div" className="error-message"/>
                </div>
              )}

              {(values.role === ROLES.GROUND_ADMIN) && (
                <div className="form-group">
                  <label htmlFor="grounds">
                    Assign Grounds
                  </label>
                  {groundsLoading ? (
                    <div style={{color: '#888', padding: '8px 0'}}>Loading grounds...</div>
                  ) : groundsError ? (
                    <div style={{color: '#d32f2f', padding: '8px 0'}}>Failed to load grounds</div>
                  ) : (
                    <MultiSelect
                      options={grounds.map(g => ({label: g.name, value: g.id}))}
                      value={grounds.filter(g => values.grounds.includes(g.id)).map(g => ({
                        label: g.name,
                        value: g.id
                      }))}
                      onChange={selected => setFieldValue('grounds', selected.map(opt => opt.value))}
                      placeholder="Select grounds..."
                    />
                  )}
                  <ErrorMessage name="grounds" component="div" className="error-message"/>
                </div>
              )}

              {/*<div className="form-group">*/}
              {/*  <label htmlFor="dob">*/}
              {/*    Date of Birth*/}
              {/*  </label>*/}
              {/*  <Field*/}
              {/*    type="date"*/}
              {/*    id="dob"*/}
              {/*    name="dob"*/}
              {/*    className={`form-control ${touched.dob && errors.dob ? 'is-invalid' : ''}`}*/}
              {/*  />*/}
              {/*  <ErrorMessage name="dob" component="div" className="error-message"/>*/}
              {/*</div>*/}

              {/*/!* Location Section *!/*/}
              {/*<div className="section-header">*/}
              {/*  <div*/}
              {/*    className="section-title-wrapper"*/}
              {/*    onClick={toggleLocationExpansion}*/}
              {/*    style={{cursor: 'pointer'}}*/}
              {/*  >*/}
              {/*    <button*/}
              {/*      type="button"*/}
              {/*      className="expand-btn"*/}
              {/*      onClick={(e) => {*/}
              {/*        e.stopPropagation();*/}
              {/*        toggleLocationExpansion();*/}
              {/*      }}*/}
              {/*      aria-label={expandedLocation ? 'Collapse location' : 'Expand location'}*/}
              {/*    >*/}
              {/*    <span className={`expand-icon ${expandedLocation ? 'expanded' : ''}`}>*/}
              {/*      {expandedLocation ? <IoChevronUp/> : <IoChevronDown/>}*/}
              {/*    </span>*/}
              {/*    </button>*/}
              {/*    <h2>Location Information</h2>*/}
              {/*  </div>*/}
              {/*</div>*/}

              {/*{expandedLocation && (*/}
              {/*  <div className="location-content">*/}
              {/*    <div className="form-group">*/}
              {/*      <label htmlFor="location.address">*/}
              {/*        Address <span className="required">*</span>*/}
              {/*      </label>*/}
              {/*      <Field*/}
              {/*        as="textarea"*/}
              {/*        id="location.address"*/}
              {/*        name="location.address"*/}
              {/*        className={`form-control ${touched.location?.address && errors.location?.address ? 'is-invalid' : ''}`}*/}
              {/*        placeholder="Enter address"*/}
              {/*        rows="3"*/}
              {/*      />*/}
              {/*      <ErrorMessage name="location.address" component="div" className="error-message"/>*/}
              {/*    </div>*/}
              {/*    <div className="form-group">*/}
              {/*      <label htmlFor="location.city">*/}
              {/*        City <span className="required">*</span>*/}
              {/*      </label>*/}
              {/*      <Field*/}
              {/*        as="select"*/}
              {/*        id="location.city"*/}
              {/*        name="location.city"*/}
              {/*        className={`form-control ${touched.location?.city && errors.location?.city ? 'is-invalid' : ''}`}*/}
              {/*        disabled={citiesLoading}*/}
              {/*      >*/}
              {/*        <option value="">Select a city</option>*/}
              {/*        {cities.map((city) => (*/}
              {/*          <option key={city.id} value={city.id}>*/}
              {/*            {city.name}*/}
              {/*          </option>*/}
              {/*        ))}*/}
              {/*      </Field>*/}
              {/*      {citiesLoading && <div className="loading-text">Loading cities...</div>}*/}
              {/*      <ErrorMessage name="location.city" component="div" className="error-message"/>*/}
              {/*    </div>*/}
              {/*    <div className="form-row">*/}
              {/*      <div className="form-group">*/}
              {/*        <label htmlFor="location.latitude">*/}
              {/*          Latitude*/}
              {/*        </label>*/}
              {/*        <Field*/}
              {/*          type="number"*/}
              {/*          id="location.latitude"*/}
              {/*          name="location.latitude"*/}
              {/*          className={`form-control ${touched.location?.latitude && errors.location?.latitude ? 'is-invalid' : ''}`}*/}
              {/*          placeholder="Enter latitude"*/}
              {/*          step="any"*/}
              {/*        />*/}
              {/*        <ErrorMessage name="location.latitude" component="div" className="error-message"/>*/}
              {/*      </div>*/}
              {/*      <div className="form-group">*/}
              {/*        <label htmlFor="location.longitude">*/}
              {/*          Longitude*/}
              {/*        </label>*/}
              {/*        <Field*/}
              {/*          type="number"*/}
              {/*          id="location.longitude"*/}
              {/*          name="location.longitude"*/}
              {/*          className={`form-control ${touched.location?.longitude && errors.location?.longitude ? 'is-invalid' : ''}`}*/}
              {/*          placeholder="Enter longitude"*/}
              {/*          step="any"*/}
              {/*        />*/}
              {/*        <ErrorMessage name="location.longitude" component="div" className="error-message"/>*/}
              {/*      </div>*/}
              {/*    </div>*/}
              {/*  </div>*/}
              {/*)}*/}

              {userData && (
                <div className="info-section">
                  <div className="info-item">
                    <span className="info-label">User ID:</span>
                    <span className="info-value">{userData.id}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Created:</span>
                    <span className="info-value">
                    {new Date(userData.created).toLocaleDateString()}
                  </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Modified:</span>
                    <span className="info-value">
                    {new Date(userData.modified).toLocaleDateString()}
                  </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Last Login:</span>
                    <span className="info-value">
                    {userData.last_login ? new Date(userData.last_login).toLocaleString() : 'Never'}
                  </span>
                  </div>
                </div>
              )}

              <div className="actions-section">
                <button
                  className="submit-btn primary"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? <ComLoader/> : "Save Changes"}
                </button>

                <button
                  className="submit-btn secondary"
                  type="button"
                  onClick={handleCancel}
                  disabled={loading || deleteLoading}
                >
                  Cancel
                </button>

                <button
                  className="submit-btn danger"
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={loading || deleteLoading}
                >
                  Delete User
                </button>
              </div>
            </Form>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showDeleteModal}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        loading={deleteLoading}
        variant="danger"
      />
    </div>
  );
}

export default UserDetail;